/**
 * A language result, drawn — the overall CEFR band large, the skills beside or
 * under it, and what stands behind it: "Self-declared", or the analyser's
 * confidence in the certificate. The citizen's TestIt page and the company's
 * Vetting drawer draw a language with this one piece, so a level reads the
 * same wherever it is looked at.
 *
 *   `default` — a result read in full (a dialog, a sheet): the band in a tile
 *               with what it is and its name, the skills as a grid of tiles
 *               under it, each on the CEFR scale (A1 → C2);
 *   `compact` — a result inside a row or a card: the band, its name and flags
 *               beside it, the skills as a small table on the same line when
 *               there is room.
 *
 * Presentation only: the bands and every word are the caller's, translated
 * (`readCefrResult` / `cefrSkillRows` in @tonnie/heroes read them out of a
 * stored result). Inside a hero's ToneScope the band wears the hero's tone.
 */
import { ShieldCheck } from '@tonnie/assets';
import type { ReactNode } from 'react';

import { cn } from '../lib/utils';
import { Badge } from '../primitives/badge';

/** What a skill that was not assessed shows — typography, not words. */
const NOT_ASSESSED = '—';

/** The CEFR scale, lowest first — the steps a skill's band is drawn on. */
const CEFR_SCALE = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;

/** How far up the scale a band sits (1–6); a native speaker tops it; null off the scale. */
function scaleStep(level: string | null): number | null {
  if (level == null) return null;
  const band = level.trim().toUpperCase();
  if (band === 'NATIVE') return CEFR_SCALE.length;
  const at = CEFR_SCALE.findIndex((step) => step === band);
  return at === -1 ? null : at + 1;
}

/** A band on the scale: six steps, those up to it filled in the scope's tone. */
function ScaleSteps({ level }: { level: string | null }) {
  const step = scaleStep(level);
  if (step == null) return null;
  return (
    <span className="mt-1.5 flex gap-0.5" aria-hidden="true" data-cefr-step={step}>
      {CEFR_SCALE.map((band, i) => (
        <span
          key={band}
          className={cn('h-1 flex-1 rounded-pill', i < step ? 'bg-primary' : 'bg-muted')}
        />
      ))}
    </span>
  );
}

export interface CefrSkillLevel {
  /** Stable id (`reading`, `listening`, …). */
  id: string;
  /** The skill's name, translated. */
  label: string;
  /** Its band; null when it was not assessed. */
  level: string | null;
}

export interface CefrBreakdownProps {
  /** The overall band (`B2`, `Native`); null when none was stated. */
  overall: string | null;
  /** What the band is ("Overall level") — said beside it in `default`, its accessible name in `compact`. */
  overallLabel: string;
  /** The band's name ("Advanced" for C1), translated; said beside it when given. */
  overallName?: string | null;
  skills: readonly CefrSkillLevel[];
  /** "Self-declared" — given only for a level with no test behind it. */
  selfDeclaredLabel?: string;
  /** "Analyser confidence: 84%" — given only when the analyser stated one. */
  confidenceLabel?: string;
  /** Beside the band in `compact` — a row's name for the language. */
  heading?: ReactNode;
  /** Skills that were not assessed: a dash in their place, or left out. */
  unassessed?: 'dash' | 'hide';
  density?: 'default' | 'compact';
  className?: string;
  /** On the root; the confidence line carries `<testId>-confidence`. */
  'data-testid'?: string;
}

export function CefrBreakdown({
  overall,
  overallLabel,
  overallName,
  skills,
  selfDeclaredLabel,
  confidenceLabel,
  heading,
  unassessed = 'dash',
  density = 'default',
  className,
  'data-testid': testId,
}: CefrBreakdownProps) {
  const shown = unassessed === 'hide' ? skills.filter((s) => s.level != null) : skills;
  const compact = density === 'compact';

  const flags =
    selfDeclaredLabel || confidenceLabel ? (
      <>
        {selfDeclaredLabel ? (
          <Badge variant="outline" className="mt-1 gap-1 text-2xs">
            <ShieldCheck className="h-3 w-3" aria-hidden="true" />
            {selfDeclaredLabel}
          </Badge>
        ) : null}
        {confidenceLabel ? (
          <p
            className="mt-1 text-xs text-muted-foreground"
            data-testid={testId ? `${testId}-confidence` : undefined}
          >
            {confidenceLabel}
          </p>
        ) : null}
      </>
    ) : null;

  if (compact) {
    return (
      <div
        className={cn('flex flex-wrap items-center gap-x-4 gap-y-2', className)}
        data-testid={testId}
        data-cefr-overall={overall ?? undefined}
      >
        <span className="flex h-10 min-w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 px-1.5 text-base font-bold text-primary-accessible">
          {/* Said, not shown: the tile's band is the overall level. */}
          <span className="sr-only">{overallLabel} </span>
          {overall ?? NOT_ASSESSED}
        </span>
        {heading || overallName || flags ? (
          <div className="min-w-0 flex-1">
            {heading}
            {overallName ? <p className="text-sm font-semibold">{overallName}</p> : null}
            {flags}
          </div>
        ) : null}
        {shown.length > 0 ? (
          <dl
            className={cn(
              'grid gap-3 text-center',
              shown.length >= 4 ? 'grid-cols-4' : shown.length === 3 ? 'grid-cols-3' : 'grid-cols-2'
            )}
          >
            {shown.map((skill) => (
              <div key={skill.id} className="min-w-0">
                <dt className="truncate text-2xs text-muted-foreground">{skill.label}</dt>
                <dd className="text-sm font-semibold tabular-nums">
                  {skill.level ?? NOT_ASSESSED}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={cn('space-y-3', className)}
      data-testid={testId}
      data-cefr-overall={overall ?? undefined}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-14 min-w-14 items-center justify-center rounded-lg bg-primary/10 px-2 text-2xl font-bold text-primary-accessible">
          {overall ?? NOT_ASSESSED}
        </div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{overallLabel}</p>
          {overallName ? <p className="text-base font-semibold">{overallName}</p> : null}
          {flags}
        </div>
      </div>
      {shown.length > 0 ? (
        <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {shown.map((skill) => (
            <div key={skill.id} className="rounded-md border border-border bg-muted/30 px-3 py-2">
              <dt className="text-2xs text-muted-foreground">{skill.label}</dt>
              <dd className="text-base font-semibold tabular-nums">
                {skill.level ?? NOT_ASSESSED}
                <ScaleSteps level={skill.level} />
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  );
}
